<template>
  <div class="details pb-5">
    <div class="details__title details__title--no-height">
      <div class="wrapper container" v-if="details && !isLoading">
        <span
          class="is-flex is-justify-content-space-between is-align-content-baseline is-flex-direction-row"
          v-if="isHotelReservation() && !missingOfferData"
        >
          <button
            @click="gotoHotelDetail()"
            class="c-button c-button--secondary c-button-small"
          >
            {{ $t("backToHotel") }}
          </button>
        </span>
        <h1 v-else-if="reservationType === 'city'">
          {{ $t("ReservationForm.requestCityReservation", { city }) }}
        </h1>
      </div>
    </div>
    <div
      class="details__content container loading-container"
      v-if="isLoading && !isPricesLoading"
    >
      <h3 class="title is-3">{{ $t("ReservationForm.loading") }}</h3>
      <br />
      <img width="50" height="60" src="@/assets/images/loader.svg" />
    </div>
    <div v-else-if="missingOfferData && isHotelReservation()" class="container">
      <div class="details__content__box mt-5">
        <h3 class="title is-4">
          {{ $t("ReservationForm.missingOfferTitle") }}
        </h3>
        <p class="mb-5">{{ $t("ReservationForm.missingOfferMessage") }}</p>
        <button @click="gotoHotelDetail()" class="c-button c-button--primary">
          {{ $t("backToHotel") }}
        </button>
      </div>
    </div>
    <div v-else class="container">
      <div class="columns reservation-layout">
        <div class="column is-three-fifths-desktop reservation-layout__left">
          <div class="details__content__box">
            <template v-if="!isHotelReservation()">
              <h1 class="mb-3">
                {{ $t("ReservationForm.requestCityReservation", { city }) }}
              </h1>
              <p class="mb-5">
                {{ $t("ReservationForm.cityReservationIntroduction") }}
              </p>
            </template>
            <div class="mb-5">
              <div
                class="field mb-5"
                v-if="
                  !companyInfo?.userSettings?.canBookOnBehalfOfOthers ||
                  (!companyInfo.allowUnlistedEmployees &&
                    !companyInfo.allowListedEmployees)
                "
              >
                <h5 class="title is-6">
                  {{ $t("ReservationForm.personalReservationHeader") }}
                </h5>

                <p>{{ $t("ReservationForm.personalReservation") }}</p>
              </div>
              <div
                v-if="
                  companyInfo.userSettings?.canBookOnBehalfOfOthers &&
                  (companyInfo.allowListedEmployees ||
                    companyInfo.allowUnlistedEmployees)
                "
              >
                <div class="title is-5">
                  {{ $t("ReservationForm.EmployeeTitle") }} *
                </div>
                <p class="mb-3">
                  {{ $t("ReservationForm.changeRoomsMessage") }}
                  <a
                    class="u-weight--bold"
                    @click.prevent="gotoHotelDetail()"
                    >{{ $t("ReservationForm.hotelPageLinkText") }}</a
                  >
                </p>
                <div class="room-assign">
                  <div
                    v-for="roomIndex in totalRoomCount"
                    :key="roomIndex"
                    class="room-assign__block"
                    :class="{
                      'room-assign__block--assigned':
                        roomAssignments[roomIndex - 1]?.employee
                    }"
                    @click="openAssignModal(roomIndex - 1)"
                  >
                    <div class="room-assign__header">
                      <span class="room-assign__label">
                        <i class="fas fa-bed"></i>
                        {{
                          $t("ReservationForm.roomLabel", { number: roomIndex })
                        }}
                      </span>
                    </div>
                    <div
                      v-if="roomAssignments[roomIndex - 1]?.employee"
                      class="room-assign__employee"
                    >
                      <span class="room-assign__employee-name">
                        <i class="fas fa-user"></i>
                        {{ roomAssignments[roomIndex - 1].employee.firstName }}
                        {{ roomAssignments[roomIndex - 1].employee.lastName }}
                        <template
                          v-if="
                            roomAssignments[roomIndex - 1].employee._unlisted
                          "
                        >
                          ({{
                            roomAssignments[roomIndex - 1].employee
                              .emailAddress
                          }})
                        </template>
                      </span>
                      <button
                        class="c-button c-button--small is-danger p-1 room-assign__remove"
                        @click.stop.prevent="removeAssignment(roomIndex - 1)"
                      >
                        <span class="icon is-small is-danger">
                          <i class="fas fa-times"></i>
                        </span>
                      </button>
                    </div>
                    <div v-else class="room-assign__placeholder">
                      <i class="fas fa-user-plus"></i>
                      {{ $t("ReservationForm.assignEmployee") }}
                    </div>
                  </div>
                </div>

                <EmployeeAssignModal
                  :show="assignModalOpen"
                  :room-number="assignModalRoomIndex + 1"
                  :business-units="businessUnits"
                  :company-info="companyInfo"
                  :already-assigned-employees="allAssignedListedEmployees"
                  :is-checking="assignModalChecking"
                  :overlap-error="assignModalOverlapError"
                  @close="closeAssignModal"
                  @assign-employee="handleModalAssignEmployee"
                  @assign-unlisted-employee="handleModalAssignUnlisted"
                  @confirm-assign="confirmModalAssign"
                  @clear-pending="clearAssignModalPending"
                />
              </div>
            </div>

            <form @submit.prevent="makeReservation()">
              <div>
                <div class="field mb-5 mt-6">
                  <ExpensesManager
                    :expenses="expenses"
                    :can-edit-expenses="canEditExpenses"
                    @update-expense="handleUpdateExpense"
                    @handle-price-input="handleExpensePriceInput"
                    @handle-quantity-input="handleExpenseQuantityInput"
                  />
                  <div
                    class="title is-5"
                    v-if="
                      businessUnits?.length > 1 ||
                      companyInfo.customFields.length > 0
                    "
                  >
                    {{ $t("ReservationForm.billingDetails") }}
                  </div>
                  <CustomField
                    v-for="field in companyInfo.customFields"
                    :key="field.name"
                    :customField="field"
                    :customFieldFormData="customFieldFormData"
                    @setted-value="setValue"
                  ></CustomField>
                  <SelectedBusinessUnit
                    :business-units="businessUnits"
                    :selected-business-unit="selectedBusinessUnit"
                    :preselected-business-unit-id="preselectedBusinessUnitId"
                    heading-tag="div"
                    heading-class="u-weight--bold mt-5"
                    required
                    @selected-business-unit="selectBusinessUnit"
                    @remove-business-unit="removeBusinessUnit"
                  />
                </div>
                <label for="notes">{{ $t("ReservationForm.notes") }} </label>
                <div class="control">
                  <textarea
                    class="textarea"
                    v-model="notes"
                    id="notes"
                    name="notes"
                    type="text"
                    rows="5"
                    @input="detectLateCheckoutInNotes"
                    :placeholder="$t('ReservationForm.notesPlaceholder')"
                  ></textarea>
                </div>
                <!-- Late checkout checkbox -->
                <span class="checkbox--filter mt-4">
                  <input
                    id="late-checkout"
                    type="checkbox"
                    v-model="lateCheckout"
                  />
                  <label
                    class="mb-0 checkbox--filter--label u-weight--normal"
                    for="late-checkout"
                  >
                    {{ $t("ReservationForm.lateCheckout") }}
                  </label>
                </span>
                <div v-if="lateCheckout" class="notification mt-2">
                  {{ $t("ReservationForm.lateCheckoutDetected") }}
                </div>
                <div class="field" v-if="isHotelReservation()">
                  <AlertMessage
                    :prefix="$t('ReservationForm.overlapErrorMessage')"
                    :message="overlappingDateErrors"
                  />
                  <AlertMessage :message="availabilityCheckError" />

                  <span class="checkbox--filter mb-2 mt-4">
                    <input
                      id="filter-bookingrules"
                      v-model="bookingRulesAgreed"
                      type="checkbox"
                    />
                    <label
                      for="filter-bookingrules"
                      class="checkbox--filter--label u-weight--normal"
                    >
                      {{ $t("bookingRulesAgreed") }}
                      <a
                        href="/booking-rules"
                        target="_blank"
                        rel="noopener noreferrer"
                        >{{ $t("bookingRules") }}</a
                      >
                      *
                    </label>
                  </span>
                  <div class="control is-flex is-align-content-baseline">
                    <button
                      type="submit"
                      class="c-button c-button--primary is-medium"
                      :disabled="isSaving || disableButton"
                    >
                      <span
                        :title="
                          $t(isDirectBooking ? 'bookNow' : 'requestReservation')
                        "
                      >
                        {{
                          $t(isDirectBooking ? "bookNow" : "requestReservation")
                        }}
                      </span>
                    </button>
                    <template v-if="isSaving">
                      &nbsp;&nbsp;&nbsp;
                      <img
                        width="50"
                        height="50"
                        src="@/assets/images/loader.svg"
                      />
                    </template>
                  </div>
                </div>
                <div class="field" v-if="!isHotelReservation()">
                  <p class="mb-5">
                    {{ $t("enteredAllWishes") }}
                  </p>
                  <div class="control is-flex is-align-content-baseline">
                    <button
                      type="submit"
                      :disabled="isSaving || disableButton"
                      class="c-button c-button--primary is-medium"
                    >
                      <span
                        :title="
                          $t(isDirectBooking ? 'bookNow' : 'requestReservation')
                        "
                      >
                        {{
                          $t(isDirectBooking ? "bookNow" : "requestReservation")
                        }}
                      </span>
                    </button>
                    <template v-if="isSaving">
                      &nbsp;&nbsp;&nbsp;
                      <img
                        width="50"
                        height="50"
                        src="@/assets/images/loader.svg"
                      />
                    </template>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
        <div class="column is-two-fifths-desktop reservation-layout__right">
          <div class="details__content__box">
            <div class="room-card__carousel room-card__carousel--filling">
              <img
                v-if="details.photos && details.photos.length"
                class="room-card__image"
                :src="details.photos[currentImageIndex]"
                :alt="details.roomName"
              />
              <div
                v-else
                class="room-card__image room-card__image--placeholder is-flex is-justify-content-center is-align-items-center"
              >
                {{ $t("hasNoImages") }}
              </div>

              <template v-if="details.photos && details.photos.length > 1">
                <button
                  class="room-card__arrow room-card__arrow--left"
                  type="button"
                  @click="prevImage"
                  :disabled="currentImageIndex <= 0"
                  aria-label="Previous photo"
                >
                  <i class="fas fa-chevron-left"></i>
                </button>
                <button
                  class="room-card__arrow room-card__arrow--right"
                  type="button"
                  @click="nextImage"
                  :disabled="currentImageIndex >= details.photos.length - 1"
                  aria-label="Next photo"
                >
                  <i class="fas fa-chevron-right"></i>
                </button>
                <span class="room-card__counter">
                  {{ currentImageIndex + 1 }} / {{ details.photos.length }}
                </span>
              </template>
            </div>
            <h1 class="title is-5">{{ details.name }}</h1>
            <div class="reservation-room-info" v-if="roomInfoCheckinDate">
              <div
                class="title u-weight--bold is-6 mb-1"
                v-if="roomInfoRoomName"
              >
                {{ roomInfoRoomName }}
              </div>
              <p v-if="roomInfoRoomName" class="mb-3">
                {{ $t("ReservationForm.bedTypeDisclaimer") }}
              </p>
              <div class="is-flex is-justify-content-space-between">
                <div class="is-flex is-flex-direction-column">
                  <div class="u-weight--bold">
                    {{ $t("checkInFrom") }}
                    <div
                      class="c-icon-info c-icon-info--border js-tooltip is-inline-block c-icon-info--small mb-0"
                      data-tooltip-position="top"
                      v-if="getPolicies().checkOutPolicy"
                      :data-tooltip-content="
                        getPolicies().checkInPolicy.join('<br />')
                      "
                    ></div>
                  </div>
                  <div>
                    {{ formatRoomInfoDate(roomInfoCheckinDate) }}
                  </div>
                  <div>{{ details.policies.checkInStartTime }}</div>
                </div>
                <div class="is-flex is-flex-direction-column">
                  <div class="u-weight--bold">
                    {{ $t("checkOutBefore") }}
                    <div
                      class="c-icon-info c-icon-info--border js-tooltip is-inline-block c-icon-info--small mb-0"
                      data-tooltip-position="top"
                      v-if="getPolicies().checkOutPolicy"
                      :data-tooltip-content="
                        getPolicies().checkOutPolicy.join('<br />')
                      "
                    ></div>
                  </div>
                  <div>
                    {{ formatRoomInfoDate(roomInfoCheckoutDate) }}
                  </div>
                  <div>{{ details.policies.checkOutTime }}</div>
                </div>
                <div class="is-flex is-flex-direction-column">
                  <div class="u-weight--bold">
                    {{ $t("ReservationForm.nights") }}
                  </div>
                  <div>
                    {{ roomInfoNights }}
                  </div>
                </div>
              </div>
              <ContentList
                :title="'ReservationForm.checkInInstructions'"
                :content-list="getPolicies().checkInInstructions"
                v-if="getPolicies().checkInInstructions"
                :smallText="true"
                class="mt-5"
              />
              <ContentList
                :title="'expedia.goodToKnow'"
                :content-list="combinedSpecialInstructions"
                v-if="combinedSpecialInstructions.length"
                :smallText="true"
                :plainText="true"
                class="mt-5"
              />
            </div>
          </div>
          <div class="details__content__box mt-5 mb-0">
            <h1 class="title is-5 mt-0">
              {{ $t("ReservationForm.priceDetail") }}
            </h1>
            <div
              class="is-flex is-justify-content-space-between is-align-items-center mb-2"
            >
              <span>
                {{ $t("nightsWithLabel", { count: roomInfoNights }) }} x
                {{ toPriceLabel(selectedOfferNightlyDisplay) }}
              </span>
              <span
                class="u-weight--bold is-flex is-align-items-center is-justify-content-end"
              >
                {{ toPriceLabel(selectedOfferRoomSubtotal) }}
              </span>
            </div>
            <div
              v-if="shouldShowTaxes && selectedOfferCityTaxTotal > 0"
              class="is-flex is-justify-content-space-between is-align-items-center mb-2"
            >
              {{ $t("ReservationForm.expenseTourismTax") }}

              <span
                class="is-flex is-align-items-center is-justify-content-end u-weight--bold"
              >
                {{ toPriceLabel(selectedOfferCityTaxTotal) }}
              </span>
            </div>
            <div
              v-if="shouldShowTaxes"
              class="is-flex is-justify-content-space-between is-align-items-center mb-2"
            >
              <span>{{ $t("ReservationForm.taxesTotal") }}</span>
              <span
                class="u-weight--bold is-flex is-align-items-center is-justify-content-end"
              >
                {{ toPriceLabel(selectedOfferTaxTotal) }}
              </span>
            </div>
            <hr class="mt-2 mb-2" />
            <div
              class="is-flex is-justify-content-space-between is-align-items-center"
            >
              <span class="u-font-size--normal u-weight--bold">{{
                $t("total")
              }}</span>
              <span
                class="is-flex is-align-items-end is-justify-content-end is-flex-direction-column"
              >
                <span class="u-font-size--normal u-weight--bold">{{
                  toPriceLabel(selectedOfferTotalPrice)
                }}</span>
              </span>
            </div>
            <div v-if="selectedOfferData" class="c-amenity-badges mt-4">
              <div
                class="c-amenity-badges__item"
                :class="{
                  'c-amenity-badges__item--inactive':
                    !selectedOfferData.breakfastIncluded &&
                    !breakfastSpecialRequest
                }"
              >
                <img
                  src="@/assets/images/icons/breakfast.svg"
                  width="16"
                  class="c-amenity-badges__icon--big"
                  aria-hidden="true"
                />
                <span>{{
                  $t(
                    selectedOfferData.breakfastIncluded ||
                      breakfastSpecialRequest
                      ? breakfastSpecialRequest
                        ? "breakfastAvailableOnRequest"
                        : "breakfastAvailable"
                      : "breakfastNotAvailable"
                  )
                }}</span>
              </div>
              <div
                class="c-amenity-badges__item"
                :class="{
                  'c-amenity-badges__item--inactive':
                    !selectedOfferData.refundable
                }"
              >
                <img
                  src="@/assets/images/icons/refund.svg"
                  width="16"
                  class="c-amenity-badges__icon--big"
                  aria-hidden="true"
                />
                <span
                  >{{
                    $t(
                      selectedOfferData.refundable
                        ? "offerIsRefundablePrefix"
                        : "offerIsNotRefundablePrefix"
                    )
                  }}
                  <span
                    v-if="isDirectBooking"
                    class="js-tooltip"
                    style="text-decoration: underline dashed; cursor: help"
                    data-tooltip-position="top"
                    :data-tooltip-content="
                      selectedOfferData.refundable
                        ? expediaRapidCancellationPolicy || $t('refundableInfo')
                        : $t('nonRefundableWarning')
                    "
                    >{{ $t("refundableKeyword") }}</span
                  >
                  <span v-else>{{ $t("refundableKeyword") }}</span>
                </span>
              </div>
            </div>

            <ContentList
              :title="'tarieven'"
              :content-list="getDetailFeesHtml()"
              v-if="getDetailFeesHtml()"
              :smallText="true"
              class="mt-5"
            />
          </div>
        </div>
      </div>
    </div>

    <NotificationMessage
      :level="notification.level"
      :autohide="false"
      :title="notification.title"
      :message="notification.message"
      :show="notification.show"
      @toggle="toggleNotification"
    />
  </div>
</template>

<script>
import CommonHelper from "@/helpers/common.helper.js";
import ReservationHelper from "@/helpers/reservation.helper.js";
import HotelHelper from "@/helpers/hotel.helper.js";
import {
  isExpediaRapidOffer,
  isOpenGdsOffer,
  shouldRequestReservation
} from "@/helpers/offer.helper.js";
import ContentList from "./components/hoteldetail/ContentList.vue";

import SelectedBusinessUnit from "./components/reservationform/SelectedBusinessUnit.vue";
import EmployeeAssignModal from "./components/reservationform/EmployeeAssignModal.vue";
import CustomField from "./components/reservationform/CustomField.vue";
import ExpensesManager from "./components/reservationform/ExpensesManager.vue";

import moment from "moment";

export default {
  props: {
    reservationType: {
      type: String,
      default: "hotel"
    },
    hotelId: {
      type: String,
      default: ""
    },
    locationPath: {
      type: String,
      default: ""
    },
    city: {
      type: String,
      default: ""
    },
    checkin: {
      type: String,
      default: null
    },
    nights: {
      type: String,
      default: null
    },
    adults: {
      type: String,
      default: "1"
    },
    rooms: {
      type: String,
      default: "1"
    },
    preselectedBusinessUnitId: {
      type: String,
      default: null
    },
    guests: {
      type: String,
      default: ""
    },
    feedbackId: {
      type: String,
      default: null
    },
    refundable: {
      type: String,
      default: "true"
    },
    latitude: {
      type: String,
      default: null
    },
    longitude: {
      type: String,
      default: null
    },
    breakfastIncluded: {
      type: String,
      default: "false"
    }
  },
  components: {
    SelectedBusinessUnit,
    EmployeeAssignModal,
    CustomField,
    ExpensesManager,
    ContentList
  },
  data() {
    return {
      isLoading: true,
      isPricesLoading: false,
      showCheckInInstructions: false,
      disableButton: false,
      details: {},
      notes: "",
      fetchedFeedbackId: null,
      budget: null,
      saveData: null,
      selectedOfferData: null,
      routerOfferData: null,
      returnedData: null,
      startDate: "",
      endDate: "",
      startDateString: "",
      endDateString: "",
      currentImageIndex: 0,
      businessUnits: null,
      allowBusinessUnitClearing: false,
      selectedEmployees: [],
      selectedUnlistedEmployees: [],
      roomAssignments: [],
      assignModalOpen: false,
      assignModalRoomIndex: 0,
      assignModalChecking: false,
      assignModalOverlapError: "",
      assignModalPending: null,
      assignModalCheckToken: 0,
      selectedBusinessUnit: null,
      errorMessages: [],
      overlappingDateErrors: "",
      availabilityCheckError: "",
      companyInfo: {
        userSettings: {},
        customFields: []
      },
      reservationMeta: {
        checkin: moment().add(1, "days").format("YYYY-MM-DD"),
        nights: 1,
        rooms: 1,
        refundable: true,
        adults: 1,
        breakfastIncluded: false
      },
      customFieldFormData: {},
      dateRange: {
        startDate: null,
        endDate: null
      },
      isSaving: false,
      bookingRulesAgreed: false,
      lateCheckout: false,
      expenses: {
        coveredExpenses: [],
        areAllExpensesCovered: false
      }
    };
  },
  mounted() {
    window.scrollTo(0, 0);
  },
  async created() {
    await this.getCompanyInfo();
    await this.fetchCoveredExpenses();

    if (this.checkin) {
      this.reservationMeta.checkin = this.checkin;
    }
    this.reservationMeta.adults = this.adults;

    if (this.nights) {
      this.reservationMeta.nights = this.nights;
    }

    if (this.refundable == "true") {
      this.reservationMeta.refundable = true;
    }

    this.reservationMeta.rooms = this.rooms;
    this.reservationMeta.breakfastIncluded = this.breakfastIncluded;

    let dateRange = CommonHelper.getDateRange(
      this.reservationMeta.checkin,
      this.reservationMeta.nights
    );
    this.startDateString = dateRange.startDateString;
    this.endDateString = dateRange.endDateString;

    let companyInfo = await CommonHelper.getCompanyInfo();
    if (companyInfo && companyInfo.budget) {
      this.budget = companyInfo.budget;
    }

    this.dateRange = [
      moment(dateRange.startDate).toDate(),
      moment(dateRange.endDate).toDate()
    ];

    if (this.isHotelReservation()) {
      this.loadDetails();
    } else {
      setTimeout(() => {
        this.loadMap({
          latitude: this.latitude,
          longitude: this.longitude
        });
      }, 1000);
    }
  },

  computed: {
    canEditExpenses() {
      return (
        HotelHelper.canBookOnBehalfOfOthers(this.companyInfo) &&
        HotelHelper.canEditExpenses(this.companyInfo)
      );
    },
    numberOfPersons() {
      const selectedNumber =
        this.selectedEmployees.length + this.selectedUnlistedEmployees.length;
      return selectedNumber;
    },
    totalRoomCount() {
      const fromRouter = parseInt(this.routerOfferData?.rooms, 10);
      const fromMeta = parseInt(this.reservationMeta?.rooms, 10);
      return fromRouter > 0 ? fromRouter : fromMeta > 0 ? fromMeta : 1;
    },
    missingOfferData() {
      return !this.routerOfferData;
    },
    allAssignedListedEmployees() {
      return this.roomAssignments
        .filter((ra) => ra?.employee && !ra.employee._unlisted)
        .map((ra) => ra.employee);
    },
    selectedDates() {
      return CommonHelper.getDateRange(
        this.reservationMeta.checkin,
        this.reservationMeta.nights
      ).dates;
    },
    allSelectedEmployees() {
      const listed = this.selectedEmployees.map((e) => ({
        ...e,
        _unlisted: false
      }));
      const unlisted = this.selectedUnlistedEmployees.map((e) => ({
        ...e,
        _unlisted: true
      }));
      return [...listed, ...unlisted];
    },
    selectedRouterOffer() {
      return this.routerOfferData?.offer || null;
    },
    selectedOfferNightlyTotal() {
      return this.selectedRouterOffer?.nightlyTotal ?? 0;
    },
    selectedOfferNightlyBase() {
      return this.selectedRouterOffer?.nightlyBase ?? 0;
    },
    selectedOfferNightlyDisplay() {
      // When taxes are shown separately (Expedia without price agreement),
      // use the base nightly rate. For all other cases (OpenGDS, agreed price)
      // the subtotal is stayTotalInclusive, so show the inclusive nightly rate.
      // nightlyTotal is guaranteed finite by the normalization in loadDetails.
      return this.shouldShowTaxes
        ? this.selectedOfferNightlyBase
        : this.selectedOfferNightlyTotal;
    },
    selectedOfferRoomCount() {
      return this.totalRoomCount;
    },
    selectedOfferTaxesAndFees() {
      return this.selectedRouterOffer?.nightlyTaxesAndFees ?? null;
    },
    shouldShowTaxes() {
      const isExpediaOffer = isExpediaRapidOffer(this.selectedRouterOffer);
      const hasPriceAgreement = !!this.routerOfferData?.hasAgreedPrice;
      return (
        isExpediaOffer && !hasPriceAgreement && this.selectedOfferTaxTotal > 0
      );
    },
    selectedOfferRoomSubtotal() {
      if (this.routerOfferData?.hasAgreedPrice) {
        return this.selectedRouterOffer?.stayTotalInclusive ?? 0;
      }
      const isExpediaOffer = isExpediaRapidOffer(this.selectedRouterOffer);
      if (!isExpediaOffer) {
        return this.selectedRouterOffer?.stayTotalInclusive ?? 0;
      }
      return this.selectedRouterOffer?.stayTotalExclusive ?? 0;
    },
    selectedOfferTaxTotal() {
      return Number(this.selectedRouterOffer?.taxesTotal) || 0;
    },
    selectedOfferCityTaxTotal() {
      return Number(this.selectedRouterOffer?.cityTaxTotal) || 0;
    },
    isDirectBooking() {
      // Prefer the explicit flag passed from HotelDetail (computed once
      // there using shouldRequestReservation). When the form is opened
      // without router state (fresh navigation), recompute from the
      // currently loaded hotel details and the selected offer.
      if (typeof this.routerOfferData?.requestReservation === "boolean") {
        return !this.routerOfferData.requestReservation;
      }
      if (this.details && this.selectedRouterOffer) {
        return !shouldRequestReservation(this.details, [
          this.selectedRouterOffer
        ]);
      }
      // Sensible fallback while data is still loading.
      return true;
    },
    breakfastSpecialRequest() {
      // Set from HotelDetail when the user ticked the "add breakfast (on
      // request)" checkbox for a chain that serves breakfast at the hotel
      // but not through Expedia. Drives the price-overview badge and the
      // manual-request routing.
      return !!this.routerOfferData?.breakfastSpecialRequest;
    },
    selectedOfferTotalPrice() {
      return this.selectedRouterOffer?.stayTotalInclusive ?? 0;
    },
    selectedOfferPricePerNightForRooms() {
      return this.selectedRouterOffer?.nightlyTotal ?? 0;
    },
    combinedSpecialInstructions() {
      const policies = this.details?.policies;
      if (!policies) return [];
      return [
        ...(policies.specialInstructions || []),
        ...(policies.knowBeforeYouGo || [])
      ];
    },
    roomInfoCheckinDate() {
      return this.routerOfferData?.checkin || this.reservationMeta?.checkin;
    },
    roomInfoNights() {
      return (
        parseInt(
          this.routerOfferData?.nights ?? this.reservationMeta?.nights ?? 1,
          10
        ) || 1
      );
    },
    roomInfoCheckoutDate() {
      if (!this.roomInfoCheckinDate) return null;
      const dateRange = CommonHelper.getDateRange(
        this.roomInfoCheckinDate,
        this.roomInfoNights
      );
      return dateRange?.endDate || null;
    },
    roomInfoRoomName() {
      if (!Array.isArray(this.details?.expediaRapidRooms)) {
        return null;
      }

      const selectedRateId =
        this.selectedRouterOffer?.rateId || this.selectedOfferData?.rateId;
      const priceCheckHref =
        this.selectedRouterOffer?.priceCheckHref ||
        this.selectedOfferData?.priceCheckHref;
      const fallbackRateId = this.extractRateIdFromHref(priceCheckHref);
      const normalizedPriceCheckHref = priceCheckHref
        ? decodeURIComponent(priceCheckHref).trim()
        : null;

      const matchingRoom = this.details.expediaRapidRooms.find((room) =>
        Array.isArray(room?.offers)
          ? room.offers.some((offer) => {
              if (!offer) return false;

              return this.isMatchingOfferSelection(
                offer,
                selectedRateId,
                fallbackRateId,
                normalizedPriceCheckHref
              );
            })
          : false
      );

      return matchingRoom?.roomName || null;
    },
    expediaRapidCancellationPolicy() {
      // Build cancellation policy text from the offer's cancellation fields
      const offer = this.selectedOfferData;
      if (offer?.cancellationType) {
        const text = this.buildCancellationText(offer);
        if (text) return text;
      }

      // Fallback: check the offer passed from HotelDetail
      const offerPolicy = offer?.cancellationPolicy;
      if (typeof offerPolicy === "string" && offerPolicy.trim().length > 0) {
        return offerPolicy;
      }

      const expediaRapidRoomOffers = Array.isArray(
        this.details?.expediaRapidRooms
      )
        ? this.details.expediaRapidRooms
            .flatMap((room) => room?.offers || [])
            .filter(isExpediaRapidOffer)
        : [];

      if (!expediaRapidRoomOffers.length) {
        return null;
      }

      // Try to find a matching offer with cancellation fields
      const selectedOfferName = this.selectedOfferData?.offerName;
      if (selectedOfferName) {
        const matchingOffer = expediaRapidRoomOffers.find(
          (o) =>
            o.offerName &&
            o.offerName.toLowerCase() === selectedOfferName.toLowerCase() &&
            o.cancellationType
        );
        if (matchingOffer) {
          const text = this.buildCancellationText(matchingOffer);
          if (text) return text;
        }
      }

      const hasPolicy = (o) =>
        typeof o?.cancellationPolicy === "string" &&
        o.cancellationPolicy.trim().length > 0;

      const fallbackOffer = expediaRapidRoomOffers.find((o) => hasPolicy(o));
      return fallbackOffer?.cancellationPolicy || null;
    }
  },
  methods: {
    buildCancellationText(offer) {
      if (!offer || !offer.cancellationType) return null;
      const type = offer.cancellationType;
      const date = offer.freeCancellationUntilDisplay;
      const value = offer.cancellationValue;
      if (type === "non_refundable") {
        return this.$t("nonRefundableWarning");
      }
      if (!date) return null;
      if (type === "nights") {
        return this.$t("cancellationPolicyNights", { date, value });
      }
      if (type === "percent") {
        return this.$t("cancellationPolicyPercent", { date, value });
      }
      if (type === "amount") {
        return this.$t("cancellationPolicyAmount", { date, value });
      }
      return null;
    },
    extractRateIdFromHref(href) {
      if (!href || typeof href !== "string") return null;
      const match = href.match(/\/rates\/([^?]+)/);
      return match?.[1] || null;
    },
    isMatchingOfferSelection(
      offer,
      selectedRateId,
      fallbackRateId,
      normalizedPriceCheckHref
    ) {
      if (!offer) return false;

      const offerRateId =
        offer.rateId || this.extractRateIdFromHref(offer.priceCheckHref);
      if (selectedRateId && offerRateId === selectedRateId) {
        return true;
      }

      if (!selectedRateId && fallbackRateId && offerRateId === fallbackRateId) {
        return true;
      }

      if (normalizedPriceCheckHref && offer.priceCheckHref) {
        return (
          decodeURIComponent(offer.priceCheckHref).trim() ===
          normalizedPriceCheckHref
        );
      }

      return false;
    },
    getPolicies() {
      return this.details?.policies || null;
    },
    getDetailFeesHtml() {
      const policies = this.getPolicies();
      const feeParts = [
        ...(policies?.paymentPolicy?.propertyFees || []),
        ...(policies?.paymentPolicy?.optionalExtras || [])
      ].filter((fee) => typeof fee === "string" && fee.trim().length > 0);
      return feeParts.length > 0 ? feeParts.join("<br />") : null;
    },
    isGroupBookingRequired() {
      // From 10+ people a group booking is required, different cancel terms and prices
      return (
        this.selectedEmployees.length + this.selectedUnlistedEmployees.length >
        10
      );
    },
    handleDate(data) {
      let checkin = moment(data[0]).set("hour", 12);
      let endDate = moment(data[1]).set("hour", 12);

      let nights = endDate.diff(checkin, "days");
      if (nights == 0) nights = 1;

      let dateRange = CommonHelper.getDateRange(moment(checkin), nights);

      this.reservationMeta.nights = parseInt(nights);
      this.reservationMeta.checkin = checkin.format("YYYY-MM-DD");

      this.startDateString = dateRange.startDateString;
      this.endDateString = dateRange.endDateString;
    },
    isHotelReservation() {
      return this.reservationType === "hotel";
    },

    async loadDetails(clearFeedbackId = false) {
      // Load offer data passed via router state from HotelDetail or SearchResult
      const offerData = history.state?.offerData;
      if (offerData) {
        // Normalize offer properties: SearchResult passes API-shaped offers
        // (price, isRefundable, isBreakfastIncluded) while HotelDetail passes
        // transformed offers (nightlyBase, refundable, breakfastIncluded).
        if (offerData.offer) {
          const o = offerData.offer;
          if (o.nightlyBase === undefined && o.nightlyPrice !== undefined) {
            o.nightlyBase = o.nightlyPrice;
            o.nightlyTotal = o.nightlyPrice;
          }
          // Ensure nightlyTotal is a finite number so the price breakdown never
          // displays NaN. For OpenGds offers nightlyBase === nightlyTotal, so
          // it is a safe fallback; for Expedia offers shouldShowTaxes=true so
          // nightlyTotal is never used in the per-night display anyway.
          if (!Number.isFinite(o.nightlyTotal)) {
            o.nightlyTotal = Number.isFinite(o.nightlyBase) ? o.nightlyBase : 0;
          }
          if (o.stayTotalInclusive === undefined && o.price !== undefined) {
            o.stayTotalInclusive = o.price;
            o.stayTotalExclusive = o.price;
          }
          if (o.reservationType === undefined && o.supplierName !== undefined) {
            o.reservationType = o.supplierName;
          }
          if (o.refundable === undefined && o.isRefundable !== undefined) {
            o.refundable = o.isRefundable;
          }
          if (
            o.breakfastIncluded === undefined &&
            o.isBreakfastIncluded !== undefined
          ) {
            o.breakfastIncluded = o.isBreakfastIncluded;
          }
        }
        this.routerOfferData = offerData;
        this.selectedOfferData = offerData.offer || null;
      }
      this.initRoomAssignments();

      let postData = {};
      postData.checkin = moment(this.reservationMeta.checkin).format(
        "YYYY-MM-DD"
      );
      postData.nights = parseInt(this.reservationMeta.nights);
      if (this.refundable || this.refundable === "false") {
        postData.refundable = false;
      } else {
        postData.refundable = true;
      }
      const requestedAdults =
        this.numberOfPersons > 0
          ? this.numberOfPersons
          : parseInt(this.reservationMeta.adults, 10) || 1;
      const requestedRooms = this.totalRoomCount;
      postData.adults = requestedAdults;
      postData.rooms = requestedRooms;
      if (this.fetchedFeedbackId) {
        postData.feedbackId = this.fetchedFeedbackId;
      } else if (this.feedbackId) {
        if (this.feedbackId != "false" && this.feedbackId != "true") {
          postData.feedbackId = this.feedbackId;
        }
      }
      if (clearFeedbackId) {
        postData.feedbackId = null;
      }
      // eslint-disable-next-line no-undef
      let url = `${this.AVAILABILITY_API_URL}/hotel/gethotelwithprices/${this.hotelId}`;
      const { status, data } = await this.axios.post(url, postData);
      if (status < 400) {
        this.fetchedFeedbackId = data.feedbackId;
        if (this.isPricesLoading) {
          this.details.expediaRapidRooms = data.expediaRapidRooms;
          this.details.hotelAgreedPrices = data.hotelAgreedPrices;
          this.isPricesLoading = false;
        } else {
          this.details = { ...data };
        }
        this.currentImageIndex = 0;
        this.isLoading = false;
      } else {
        this.showNotification = true;
        this.notification.title = "Er ging iets mis";
        this.notification.message =
          "Er gaat iets mis op de server. Probeer het later opnieuw.";
        this.notification.level = "danger";
        this.notification.show = true;
        this.isLoading = false;
      }
      this.isLoading = false;
    },
    loadMap(location) {
      // if (this.mapLoaded) return;
      // if (!document.getElementById("details-map")) return;
      // mapboxgl.accessToken = this.MAPBOX_ACCESS_TOKEN;
      // let mapCenter = [location.longitude, location.latitude];
      // const map = new mapboxgl.Map({
      //   container: "details-map",
      //   style: this.MAPBOX_STYLE,
      //   center: mapCenter,
      //   zoom: this.isHotelReservation() ? 15 : 10
      // });
      // new mapboxgl.Marker()
      //   .setLngLat([location.longitude, location.latitude])
      //   .addTo(map);
      // this.mapLoaded = true;
    },
    prevImage() {
      if (!this.details?.photos?.length) return;
      this.currentImageIndex = Math.max(0, this.currentImageIndex - 1);
    },
    nextImage() {
      if (!this.details?.photos?.length) return;
      const lastIndex = this.details.photos.length - 1;
      this.currentImageIndex = Math.min(lastIndex, this.currentImageIndex + 1);
    },
    formatRoomInfoDate(dateValue) {
      if (!dateValue) return "-";
      // Determine locale from localStorage or fallback to 'nl'
      let locale = "nl";
      if (
        localStorage.locale &&
        (localStorage.locale === "en" || localStorage.locale === "nl")
      ) {
        locale = localStorage.locale;
      }
      moment.locale(locale);
      // For Dutch: dinsdag 17 maart 2026 | For English: Tuesday, March 17, 2026
      let formatStr =
        locale === "nl" ? "dddd D MMMM YYYY" : "dddd, MMMM D, YYYY";
      let formatted = moment(dateValue).format(formatStr);
      return formatted;
    },

    setValue(data) {
      this.customFieldFormData[data.field] = data.value;
    },
    async getCompanyInfo() {
      this.companyInfo = await CommonHelper.getCompanyInfo();

      if (!this.companyInfo) {
        return;
      }

      this.companyInfo.employeeIsRequired =
        this.companyInfo.userSettings?.canBookOnBehalfOfOthers &&
        (this.companyInfo.allowUnlistedEmployees ||
          this.companyInfo.allowListedEmployees);

      if (this.companyInfo.customFields) {
        this.companyInfo.customFields.sort((a, b) => {
          return Number(a.mandatory) > Number(b.mandatory) ? -1 : 1;
        });
      }

      // Only fetch business units if companyInfo has businessUnits property
      if (this.companyInfo.businessUnits) {
        this.businessUnits = await CommonHelper.getBusinessUnits();
      } else {
        this.businessUnits = [];
      }
    },
    async fetchCoveredExpenses() {
      try {
        const businessUnitId =
          this.selectedBusinessUnit?.id || this.preselectedBusinessUnitId;
        const params = businessUnitId
          ? `?businessUnitId=${businessUnitId}`
          : "";

        const url = `${this.AVAILABILITY_API_URL}/company/covered-expenses${params}`;

        const { status, data } = await this.axios.get(url);

        if (status === 200) {
          // Store the expenses data directly matching the C# model
          this.expenses = {
            coveredExpenses: data.coveredExpenses || [],
            areAllExpensesCovered: data.areAllExpensesCovered || false
          };
        }
      } catch (error) {
        console.error("Error fetching covered expenses:", error);
      }
    },
    getExpense(expenseType) {
      // Get expense by type, or return default structure
      const expense = this.expenses.coveredExpenses.find(
        (e) => e.expenseType === expenseType
      );
      return (
        expense || {
          expenseType: expenseType,
          isCovered: false,
          maxDailyCosts: null,
          maxDailyQuantity: null
        }
      );
    },
    updateExpense(expenseType, updates) {
      // Handle "select all" toggle
      if (expenseType === "_ALL") {
        this.expenses.areAllExpensesCovered = updates.areAllExpensesCovered;
        return;
      }
      // Update or add an expense
      const index = this.expenses.coveredExpenses.findIndex(
        (e) => e.expenseType === expenseType
      );
      // Keep raw string values (with comma) during editing; only normalize on submit
      const processedUpdates = { ...updates };
      if (processedUpdates.maxDailyCosts !== undefined) {
        if (
          processedUpdates.maxDailyCosts === null ||
          processedUpdates.maxDailyCosts === ""
        ) {
          processedUpdates.maxDailyCosts = null;
        }
        // else keep raw string/number as-is
      }
      if (processedUpdates.maxDailyQuantity !== undefined) {
        if (
          processedUpdates.maxDailyQuantity === null ||
          processedUpdates.maxDailyQuantity === ""
        ) {
          processedUpdates.maxDailyQuantity = null;
        }
        // else keep raw string/number as-is
      }

      if (index >= 0) {
        // Update existing expense
        this.expenses.coveredExpenses[index] = {
          ...this.expenses.coveredExpenses[index],
          ...processedUpdates
        };
      } else {
        // Add new expense - default to maxDailyCosts (price) if isCovered is true
        const hasLimits = [
          "LUNCH",
          "DINNER",
          "DRINKS",
          "RESTAURANT",
          "EXTRAS"
        ].includes(expenseType);
        const newExpense = {
          expenseType,
          isCovered: false,
          maxDailyCosts: null,
          maxDailyQuantity: null,
          ...processedUpdates
        };

        // If checking a limit-enabled expense, default to price type with 0 value
        if (
          hasLimits &&
          processedUpdates.isCovered &&
          newExpense.maxDailyCosts === null &&
          newExpense.maxDailyQuantity === null
        ) {
          newExpense.maxDailyCosts = 0;
          newExpense.maxDailyQuantity = null;
        }

        this.expenses.coveredExpenses.push(newExpense);
      }
    },
    handlePriceInput(expenseType, raw) {
      // Sanitize: replace dot with comma, allow digits and single comma
      let cleaned = raw.replace(/\./g, ",").replace(/[^0-9,]/g, "");
      // Prevent multiple commas (keep first)
      const firstCommaIndex = cleaned.indexOf(",");
      if (firstCommaIndex !== -1) {
        cleaned =
          cleaned.substring(0, firstCommaIndex + 1) +
          cleaned.substring(firstCommaIndex + 1).replace(/,/g, "");
      }
      if (cleaned === "") {
        this.updateExpense(expenseType, {
          isCovered: true,
          maxDailyCosts: null,
          maxDailyQuantity: null
        });
      } else {
        this.updateExpense(expenseType, {
          isCovered: true,
          maxDailyCosts: cleaned
        });
      }
    },
    handleQuantityInput(expenseType, raw) {
      let cleaned = raw.replace(/\D/g, ""); // digits only for quantity
      if (cleaned === "") {
        this.updateExpense(expenseType, {
          isCovered: true,
          maxDailyQuantity: null
        });
      } else {
        this.updateExpense(expenseType, {
          isCovered: true,
          maxDailyQuantity: cleaned
        });
      }
    },
    // Wrapper methods for ExpensesManager component events
    handleUpdateExpense({ expenseType, updates }) {
      this.updateExpense(expenseType, updates);
    },
    handleExpensePriceInput({ expenseType, value }) {
      this.handlePriceInput(expenseType, value);
    },
    handleExpenseQuantityInput({ expenseType, value }) {
      this.handleQuantityInput(expenseType, value);
    },
    selectBusinessUnit(data) {
      this.selectedBusinessUnit = data.businessUnit;
    },
    removeBusinessUnit() {
      this.selectedBusinessUnit = null;
    },
    initRoomAssignments() {
      const count = this.totalRoomCount;
      if (this.roomAssignments.length !== count) {
        const newAssignments = [];
        for (let i = 0; i < count; i++) {
          newAssignments.push(this.roomAssignments[i] || { employee: null });
        }
        this.roomAssignments = newAssignments;
      }
    },
    syncEmployeesFromAssignments() {
      this.selectedEmployees = this.roomAssignments
        .filter((ra) => ra?.employee && !ra.employee._unlisted)
        .map((ra) => ra.employee);
      this.selectedUnlistedEmployees = this.roomAssignments
        .filter((ra) => ra?.employee && ra.employee._unlisted)
        .map((ra) => ra.employee);
    },
    openAssignModal(roomIndex) {
      this.assignModalRoomIndex = roomIndex;
      this.assignModalOpen = true;
    },
    handleModalAssignEmployee(data) {
      // Buffer the selection; checkOverlap will run when the user clicks Add
      this.assignModalPending = { data, unlisted: false };
      this.assignModalOverlapError = "";
      this.runAssignModalOverlapCheck(data.employee, false);
    },
    handleModalAssignUnlisted(data) {
      // Buffer the selection; checkOverlap runs when the user clicks Add
      this.assignModalPending = { data, unlisted: true };
      this.assignModalOverlapError = "";
      this.runAssignModalOverlapCheck(data.employee, true);
    },
    async runAssignModalOverlapCheck(employee, unlisted) {
      // Guard against out-of-order responses if the user swaps selections
      // again before the previous check resolves.
      const checkToken = ++this.assignModalCheckToken;
      this.assignModalChecking = true;
      const checkPayload = {
        reservationList: [
          {
            selectedDates: this.selectedDates,
            employeeIds: unlisted ? [] : [employee.id],
            guests: unlisted
              ? [
                  {
                    firstName: employee.firstName,
                    lastName: employee.lastName,
                    emailAddress: employee.emailAddress
                  }
                ]
              : []
          }
        ]
      };
      let overlapError = "";
      try {
        const { checkData, checkStatus } =
          await this.postCheckOverlap(checkPayload);
        overlapError =
          checkStatus === 200 && checkData.isValid
            ? ""
            : this.buildAssignModalOverlapError(checkData);
      } catch (e) {
        overlapError = "";
      }
      if (checkToken !== this.assignModalCheckToken) return;
      this.assignModalOverlapError = overlapError;
      this.assignModalChecking = false;
    },
    buildAssignModalOverlapError(checkData) {
      const errors = Array.isArray(checkData?.errors) ? checkData.errors : [];
      const duplicates = Array.isArray(checkData?.duplicates)
        ? checkData.duplicates
        : [];
      const isDuplicate =
        errors.some((e) => e.type === "IS_DUPLICATE") || duplicates.length > 0;
      if (isDuplicate) {
        const dates = duplicates
          .map((d) => (d.date ? moment(d.date).format("DD-MM-YYYY") : null))
          .filter(Boolean);
        if (dates.length) {
          const items = dates
            .map(
              (date) =>
                `<li>${this.$t("ReservationForm.guestAlreadyBookedOn", { date })}</li>`
            )
            .join("");
          return `<ul>${items}</ul><p>${this.$t("ReservationForm.contactUsForChange")}</p>`;
        }
      }
      return this.$t("ReservationForm.overlapErrorMessage");
    },
    confirmModalAssign() {
      if (!this.assignModalPending) return;
      const { data, unlisted } = this.assignModalPending;
      const prevEmployee =
        this.roomAssignments[this.assignModalRoomIndex]?.employee;
      if (prevEmployee && !prevEmployee._unlisted) {
        this.selectedEmployees = this.selectedEmployees.filter(
          (se) => se.id != prevEmployee.id
        );
      } else if (prevEmployee && prevEmployee._unlisted) {
        this.selectedUnlistedEmployees = this.selectedUnlistedEmployees.filter(
          (uE) => uE.id != prevEmployee.id
        );
      }
      this.roomAssignments[this.assignModalRoomIndex] = {
        employee: { ...data.employee, _unlisted: unlisted }
      };
      if (!unlisted && this.selectedBusinessUnit == null && data.businessUnit) {
        this.selectBusinessUnit(data);
      }
      this.syncEmployeesFromAssignments();
      this.closeAssignModal();
    },
    closeAssignModal() {
      this.assignModalOpen = false;
      this.clearAssignModalPending();
    },
    clearAssignModalPending() {
      this.assignModalCheckToken++;
      this.assignModalPending = null;
      this.assignModalOverlapError = "";
      this.assignModalChecking = false;
    },
    removeAssignment(roomIndex) {
      const prevEmployee = this.roomAssignments[roomIndex]?.employee;
      if (prevEmployee && !prevEmployee._unlisted) {
        this.selectedEmployees = this.selectedEmployees.filter(
          (se) => se.id != prevEmployee.id
        );
      } else if (prevEmployee && prevEmployee._unlisted) {
        this.selectedUnlistedEmployees = this.selectedUnlistedEmployees.filter(
          (uE) => uE.id != prevEmployee.id
        );
      }
      this.roomAssignments[roomIndex] = { employee: null };
      this.syncEmployeesFromAssignments();
      this.reservationMeta.rooms = this.numberOfPersons;
    },

    gotoHotelDetail() {
      this.$router.push({
        name: this.PAGES.HotelDetail,
        params: {
          locationPath: this.locationPath || "direct",
          hotelId: this.hotelId,
          checkin: this.reservationMeta.checkin,
          nights: this.reservationMeta.nights,
          adults: this.reservationMeta.adults,
          rooms: this.reservationMeta.rooms,
          refundable: this.reservationMeta.refundable,
          breakfastIncluded: this.reservationMeta.breakfastIncluded
        }
      });
    },
    getTitle(field) {
      return this.getFieldNameTranslation(field);
    },
    validateForm() {
      this.errorMessages = [];
      this.showNotification = false;

      const businessUnitMandatory = this.companyInfo.businessUnitMandatory;
      const businessUnitIsSelected = !!this.selectedBusinessUnit;

      let isValid = true;

      if (businessUnitMandatory && !businessUnitIsSelected) {
        isValid = false;
        let message = this.$t("error.businessunitMandatory");
        message = this.tbu(message);
        this.errorMessages.push(message);
      }

      const allRoomsAssigned =
        this.roomAssignments.length === this.totalRoomCount &&
        this.roomAssignments.every((ra) => ra?.employee);
      if (
        this.companyInfo.userSettings?.canBookOnBehalfOfOthers &&
        (this.companyInfo.allowListedEmployees ||
          this.companyInfo.allowUnlistedEmployees) &&
        !allRoomsAssigned
      ) {
        isValid = false;
        const message = this.$t("error.roomsNotAssigned");
        this.errorMessages.push(message);
      }

      if (!this.bookingRulesAgreed) {
        isValid = false;
        const message = this.$t("error.bookingRulesMandatory");
        this.errorMessages.push(message);
      }
      let customFieldsValid = true;
      let invalidCustomFields = [];
      const customFields = Array.isArray(this.companyInfo?.customFields)
        ? this.companyInfo.customFields
        : [];
      customFields.forEach((field) => {
        if (
          ReservationHelper.isFieldMandatory(field, this.customFieldFormData)
        ) {
          let thisFieldHasError = false;
          if (field.type === "checkbox") {
            if (
              !this.customFieldFormData[field.name] ||
              this.customFieldFormData[field.name].length < 1
            ) {
              thisFieldHasError = true;
            }
          } else {
            if (
              !this.customFieldFormData[field.name] ||
              this.customFieldFormData[field.name] == ""
            ) {
              thisFieldHasError = true;
            }
          }
          if (thisFieldHasError) {
            customFieldsValid = false;
            isValid = false;
            invalidCustomFields.push(this.getTitle(field));
          }
        }
      });

      if (!customFieldsValid) {
        this.errorMessages = [...this.errorMessages, invalidCustomFields];
      }
      if (!isValid) {
        let notificationMessage = `${this.$t("error.genericIntro")} <ul>
        <li>- ${this.errorMessages.join("</li><li>- ")}</li></ul>`;

        this.track("hotelReservationErrors", {
          error: this.errorMessages.join(", ")
        });

        this.notification.title = this.$t("error.genericTitle");
        this.notification.message = notificationMessage;
        this.notification.level = "danger";
        this.notification.show = true;

        setTimeout(() => {
          this.notification.show = false;
        }, 10 * 1000);
        return false;
      } else {
        return true;
      }
    },
    async makeReservation() {
      let selectedDates = CommonHelper.getDateRange(
        this.reservationMeta.checkin,
        this.reservationMeta.nights
      ).dates;

      let dateRange = CommonHelper.getDateRange(
        this.reservationMeta.checkin,
        this.reservationMeta.nights
      );

      let dataIsValid = this.validateForm();

      if (dataIsValid) {
        this.isSaving = true;
        const selectedOfferRateId = isOpenGdsOffer(this.selectedOfferData)
          ? Number(this.selectedOfferData?.rateId)
          : undefined;
        const selectedOfferExternalAccomodationId =
          this.selectedOfferData?.externalAccomodationId ?? 0;
        // The offer object standardizes on isRefundable / isBreakfastIncluded.
        const offerIsRefundable =
          this.selectedOfferData?.isRefundable ??
          this.selectedOfferData?.refundable ??
          this.reservationMeta.refundable ??
          false;
        let offerIsBreakfastIncluded =
          this.selectedOfferData?.isBreakfastIncluded ??
          this.selectedOfferData?.breakfastIncluded ??
          this.reservationMeta.breakfastIncluded ??
          false;
        // A breakfast special request triggers a manual booking with breakfast
        // added at the hotel, so the offer is marked breakfast-included.
        if (this.breakfastSpecialRequest) {
          offerIsBreakfastIncluded = true;
        }
        const selectedOfferPayload = this.selectedOfferData
          ? (({
              rateId,
              externalAccomodationId,
              refundable,
              breakfastIncluded,
              ...selectedOfferWithoutReservationIds
            }) => ({
              ...selectedOfferWithoutReservationIds,
              price:
                this.selectedOfferData.price ??
                this.selectedOfferData.stayTotalInclusive ??
                this.selectedOfferTotalPrice,
              nightlyTotal:
                this.selectedOfferData.nightlyTotal ??
                this.selectedOfferNightlyTotal,
              stayTotalInclusive:
                this.selectedOfferData.stayTotalInclusive ??
                this.selectedOfferTotalPrice,
              totalPrice:
                this.selectedOfferData.totalPrice ??
                this.selectedOfferData.stayTotalInclusive ??
                this.selectedOfferTotalPrice,

              // TODO: in the selectedOffer I am sending breakfastIncluded and isBreakfastIncluded. in reservationList there is breakfastIncluded as well. Together with BE consolidate into one property. For now, send all three to avoid breaking the BE. (same with refundability)
              refundable: offerIsRefundable,
              breakfastIncluded: offerIsBreakfastIncluded,
              isRefundable: offerIsRefundable,
              isBreakfastIncluded: offerIsBreakfastIncluded
            }))(this.selectedOfferData)
          : null;

        const employees = this.selectedEmployees.map((e) => e.id);
        const unlistedEmployees = this.selectedUnlistedEmployees.map((e) => {
          return {
            firstName: e.firstName,
            lastName: e.lastName,
            emailAddress: e.emailAddress
          };
        });
        const businessUnitId = this.selectedBusinessUnit
          ? this.selectedBusinessUnit.id
          : null;

        let saveData = {
          reservationList: [
            {
              customFields: this.customFieldFormData,
              selectedDates: selectedDates,
              businessUnitId: businessUnitId,
              employeeIds: employees,
              notes: this.notes,
              rooms: this.reservationMeta.rooms,
              guests: unlistedEmployees,
              hotel: {
                id: this.details.hotelId,
                name: this.details.name,
                street: this.details.address,
                postalCode: this.details.postalCode,
                city: this.details.city,
                country: this.details.country,
                checkInStartTime: this.details.policies?.checkInStartTime,
                checkInEndTime: this.details.policies?.checkInEndTime,
                checkOutTime: this.details.policies?.checkOutTime,
                isGreenKey: this.details.hasGreenKey
              },
              refundable: offerIsRefundable,
              breakfastIncluded: offerIsBreakfastIncluded,

              rateId: selectedOfferRateId,
              externalAccomodationId: selectedOfferExternalAccomodationId,
              selectedOffer: selectedOfferPayload,
              lateCheckoutRequested:
                this.lateCheckout || this.detectLateCheckoutInNotes()
            }
          ],
          reservationSystem: this.isDirectBooking ? 1 : 0
        };
        Object.keys(this.customFieldFormData).forEach((key) => {
          saveData.reservationList[0].customFields[key] =
            this.customFieldFormData[key];
        });

        // Sanitize expenses: convert 0 to null, and normalize comma decimals to numbers
        const sanitizedExpenses = {
          ...this.expenses,
          coveredExpenses: this.expenses.coveredExpenses.map((expense) => {
            const costs = expense.maxDailyCosts;
            const qty = expense.maxDailyQuantity;
            return {
              ...expense,
              maxDailyCosts:
                costs === 0 || costs === "0" || costs === ""
                  ? null
                  : typeof costs === "string"
                    ? parseFloat(costs.replace(",", "."))
                    : costs,
              maxDailyQuantity:
                qty === 0 || qty === "0" || qty === ""
                  ? null
                  : typeof qty === "string"
                    ? parseFloat(qty.replace(",", "."))
                    : qty
            };
          })
        };
        saveData.expenses = sanitizedExpenses;
        saveData.priceCheckHref =
          this.selectedOfferData?.priceCheckHref ?? null;

        this.saveData = JSON.stringify(saveData, null, " ");

        const isAvailable = await this.getAvailability();

        if (!isAvailable) {
          this.isSaving = false;
          return;
        }

        this.disableButton = true;

        let url = `${this.AVAILABILITY_API_URL}/reservation`;

        let { data, status } = await this.axios.post(url, saveData);

        if (status == 200) {
          let unlistedEmployeesCount = this.selectedUnlistedEmployees.length;
          let listedEmployeesCount = this.selectedEmployees.length;
          let employeeCount = unlistedEmployeesCount + listedEmployeesCount;

          let trackData = {
            company: this.companyInfo.company.name,
            numberOfRooms: this.reservationMeta.rooms,
            numberOfPersons: employeeCount,
            status: "success",
            errorMessages: null
          };

          trackData.fromDate = this.reservationMeta.checkin;
          trackData.nights = this.reservationMeta.nights;
          trackData.breakfastIncluded = offerIsBreakfastIncluded;
          trackData.refundable = offerIsRefundable;

          this.track("hotelReservation", trackData);
          this.returnedData = JSON.stringify(data, null, " ");

          this.notification.title = this.$t("ReservationForm.savedTitle");
          this.notification.message = this.$t("ReservationForm.savedMessage");
          this.notification.show = true;

          this.notification.level = "success";
          this.selectedEmployees = [];
          this.selectedUnlistedEmployees = [];
          this.selectedBusinessUnit = null;
          setTimeout(() => {
            this.notification.show = false;
            this.disableButton = false;
          }, 30 * 1000);
        } else {
          // Check if this is a partial error for direct booking
          if (
            data.openGdsSuccesful === true &&
            data.salesForceSuccesful === false &&
            this.isDirectBooking
          ) {
            // Redirect to dedicated partial error page
            this.isSaving = false;
            this.$router.push({ name: this.PAGES.PartialError });
            return;
          }

          // Handle all other errors
          this.notification.title = this.$t("ReservationForm.errorTitle");
          this.notification.message = this.$t("ReservationForm.errorMessage");
          this.notification.show = true;
          this.notification.level = "danger";
          this.disableButton = true;
          setTimeout(() => {
            this.notification.show = false;
          }, 30 * 1000);
          this.isSaving = false;
          return;
        }

        this.isSaving = false;
        this.$router.push("/reservation-confirmation");
      }
    },
    buildOverlapCheckPayload() {
      const selectedDates = this.selectedDates;

      const employees = this.companyInfo.userSettings?.canBookOnBehalfOfOthers
        ? this.selectedEmployees.map((e) => e.id)
        : [this.companyInfo.id];

      const businessUnitId = this.selectedBusinessUnit
        ? this.selectedBusinessUnit.id
        : null;

      const saveData = {
        reservationList: [
          {
            customFields: this.customFieldFormData,
            selectedDates,
            businessUnitId,
            employeeIds: employees,
            guests: this.selectedUnlistedEmployees,
            rooms: this.reservationMeta.rooms,
            hotel: {
              id: this.details.hotelId,
              name: this.details.name,
              street: this.details.address,
              postalCode: this.details.postalCode,
              city: this.details.city,
              country: this.details.country,
              checkInStartTime: this.details.policies?.checkInStartTime,
              checkInEndTime: this.details.policies?.checkInEndTime,
              checkOutTime: this.details.policies?.checkOutTime,
              isGreenKey: this.details.hasGreenKey
            },
            refundable:
              this.selectedOfferData?.isRefundable ??
              this.selectedOfferData?.refundable ??
              false,
            breakfastIncluded:
              this.selectedOfferData?.isBreakfastIncluded ??
              this.selectedOfferData?.breakfastIncluded ??
              false,
            priceDisplayed: this.selectedOfferData?.price,
            selectedOffer: this.selectedOfferData
          }
        ],
        reservationSystem: this.isDirectBooking ? 1 : 0
      };
      Object.keys(this.customFieldFormData).forEach((key) => {
        saveData.reservationList[0].customFields[key] =
          this.customFieldFormData[key];
      });

      return saveData;
    },
    formatOverlapEntry(overlap) {
      const date = moment(overlap.date).format("DD-MM-YYYY");

      if (overlap.employeeId != null) {
        if (!this.companyInfo.userSettings?.canBookOnBehalfOfOthers) {
          return `<li>${this.companyInfo.fullName} (${date})</li>`;
        }
        const employee = this.selectedEmployees.find(
          (employee) => employee.id == overlap.employeeId
        );
        return `<li>${employee.firstName} ${employee.lastName} (${date})</li>`;
      }
      if (overlap.guest != null) {
        return `<li>${overlap.guest.firstName} ${overlap.guest.lastName} (${date})</li>`;
      }
      return `<li>${date}</li>`;
    },
    buildOverlapErrorMessage(duplicates = []) {
      return `<ul>${duplicates.map((overlap) => this.formatOverlapEntry(overlap)).join("")}</ul>`;
    },
    onAvailabilityCheckSuccess() {
      this.overlappingDateErrors = "";
      this.availabilityCheckError = "";
      this.disableButton = false;
      return true;
    },
    onAvailabilityCheckError(checkData) {
      this.overlappingDateErrors = this.buildOverlapErrorMessage(
        checkData?.duplicates
      );
      this.availabilityCheckError = "";
      this.disableButton = false;
      return false;
    },
    onAvailabilityCheckRequestFailed(err) {
      // A failed/erroring check (network issue, server error) is not
      // evidence of an actual duplicate booking. Surface a distinct
      // message instead of reusing the overlap-detected copy, and block
      // submission until the check can be confirmed to have passed.
      console.error("Error checking reservation availability:", err);
      this.overlappingDateErrors = "";
      this.availabilityCheckError = this.$t(
        "ReservationForm.availabilityCheckErrorMessage"
      );
      this.disableButton = false;
      return false;
    },
    async postCheckOverlap(payload) {
      const checkUrl = `${this.AVAILABILITY_API_URL}/reservation/checkOverlap`;
      const response = await this.axios.post(checkUrl, payload);
      return { checkData: response?.data, checkStatus: response?.status };
    },
    async getAvailability() {
      const saveData = this.buildOverlapCheckPayload();

      this.saveData = JSON.stringify(saveData, null, " ");
      this.disableButton = true;

      let checkData;
      let checkStatus;
      try {
        ({ checkData, checkStatus } = await this.postCheckOverlap(saveData));
      } catch (err) {
        return this.onAvailabilityCheckRequestFailed(err);
      }

      if (checkStatus !== 200) {
        return this.onAvailabilityCheckRequestFailed(
          new Error(`checkOverlap responded with status ${checkStatus}`)
        );
      }

      return checkData?.isValid
        ? this.onAvailabilityCheckSuccess()
        : this.onAvailabilityCheckError(checkData);
    },

    detectLateCheckoutInNotes() {
      // Keywords to detect late checkout requests in notes
      const lateCheckoutKeywords = [
        "late checkout",
        "laat uitchecken",
        "laat uit checken",
        "late check-out",
        "late check out",
        "late uitcheck",
        "late uit check out",
        "late uit check"
      ];

      if (!this.notes) return false;

      const notesLower = this.notes.toLowerCase();

      var detected = lateCheckoutKeywords.some((keyword) =>
        notesLower.includes(keyword)
      );
      if (detected) {
        this.lateCheckout = true;
      }

      return detected;
    }
  }
};
</script>
<style scoped>
.loading-container {
  text-align: center;
  height: 80vh;
}
</style>
